import React from 'react';
import { CheckCircle2, Clock, MapPin } from 'lucide-react';
import { PARAPATHWAY_STATE_SCORES } from '../data/paraPathwayStateScores';

// Dedicated state-by-state qualifying score table for the Paraprofessional
// Pathway Assessment (ETS ParaPathways, tests 5758 + 5759). This is
// deliberately separate from the app's existing single-active-state
// passing-score mechanism (STATE_STANDARDS / userProfile.targetState),
// which only covers a narrow hand-picked list of ~10 states and isn't
// built to represent "ETS score where published, else In Progress" across
// all 50 states + DC + territories the way this assessment needs.
export const ParaPathwayScoreTable: React.FC = () => {
  const publishedCount = PARAPATHWAY_STATE_SCORES.filter(
    (s) => s.readingWritingScore !== null && s.mathScore !== null
  ).length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 text-teal-700 flex items-center justify-center shrink-0">
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Qualifying Scores by State</h2>
            <p className="text-xs text-slate-500">
              ETS-published ParaPathways qualifying scores, where set. Everywhere else reads "In Progress" rather than a guess.
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 bg-teal-50 text-teal-800 rounded-full border border-teal-200 shrink-0 self-start sm:self-auto">
          {publishedCount} of {PARAPATHWAY_STATE_SCORES.length} published
        </span>
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs border-collapse min-w-[420px]">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 px-3 font-bold">State</th>
              <th className="py-2 px-3 font-bold">Reading & Writing (5758)</th>
              <th className="py-2 px-3 font-bold">Mathematics (5759)</th>
            </tr>
          </thead>
          <tbody>
            {PARAPATHWAY_STATE_SCORES.map((s) => {
              const isPublished = s.readingWritingScore !== null && s.mathScore !== null;
              return (
                <tr key={s.code} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                  <td className="py-2 px-3 font-semibold text-slate-800">
                    {s.state}
                    <span className="text-slate-400 font-medium ml-1">({s.code})</span>
                    {s.note && (
                      <div className="text-[10px] text-slate-400 font-normal leading-snug mt-0.5 max-w-xs">{s.note}</div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {s.readingWritingScore !== null ? (
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3" /> {s.readingWritingScore}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 italic">
                        <Clock className="w-3 h-3" /> In Progress
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {s.mathScore !== null ? (
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3" /> {s.mathScore}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 italic">
                        <Clock className="w-3 h-3" /> In Progress
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-500 leading-relaxed">
        ETS's earlier ParaPro Assessment (test 1755) is being phased out state by state in favor of ParaPathways, so this page
        tracks ParaPathways only. A handful of states above still list a ParaPro-only qualifying score with ETS today -- those
        read "In Progress" here until ETS publishes a ParaPathways score for that state.
      </div>
    </div>
  );
};
